import { useDroppable } from "@dnd-kit/core";
import { Cell, Row, flexRender } from "@tanstack/react-table";
import React, { Fragment } from "react";

import CustomRightClickActionBtn from "../custom-right-click-action-btn";

import * as Table from "@/components/ui/table";
import { SELECTOR_ID } from "@/constants/global-constants";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { cn } from "@/lib/utils";

export const TableCell = <T, V>({
  cell,
  highLight,
}: {
  cell: Cell<T, V>;
  highLight: boolean;
}): React.JSX.Element => {
  return (
    <Table.TableCell
      className={cn(
        "text-black/70 dark:text-white/70",
        cell.column?.columnDef?.meta?.classNameRow,
        cell.column?.columnDef?.meta?.showRightBorder && "border-r ",
        cell.column?.columnDef?.meta?.showCustomBorder && "custom-border",
        // cell.column.columnDef.id === SELECTOR_ID && "!px-1 max-w-[30px]",
      )}
    >
      {cell.column.columnDef.id === SELECTOR_ID ? (
        <div
          className={cn(
            "invisible group-hover:visible min-h-8 flex items-center",
            highLight && "visible",
          )}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      ) : (
        flexRender(cell.column.columnDef.cell, cell.getContext())
      )}
    </Table.TableCell>
  );
};

type TableRowProps<TData, TValue> = {
  title?: string;
  row: Row<TData>;
  onRowClick?: (id: string) => void;
  RightClickData?: (row: GlobalRowDataTypes<TValue>) => ActionButtonDataTypes[];
};

const TableRow = <T extends { id?: string }, V>({
  title,
  row,
  onRowClick,
  RightClickData,
}: TableRowProps<T, V>): React.JSX.Element => {
  const RightClickComp = RightClickData ? CustomRightClickActionBtn : Fragment;

  const func = RightClickData
    ? RightClickData
    : ({} as (row: GlobalRowDataTypes<V>) => ActionButtonDataTypes[]);

  const { setNodeRef } = useDroppable({
    id: `${title}~${row.original.id}` || `~`,
  });
  const highLight = row.getIsSelected();

  return (
    <RightClickComp
      {...(RightClickData && {
        row,
        data:
          func ||
          ((): void => {
            return;
          }),
      })}
      key={row.id}
    >
      <Table.TableRow
        ref={setNodeRef}
        key={row.id}
        data-state={row.getIsSelected() && "selected"}
        className={cn("hover:bg-secondary group")}
        onClick={() =>
          onRowClick && row.original.id && onRowClick(row.original?.id)
        }
      >
        {row.getVisibleCells().map((cell, rowIdx) => {
          return (
            <TableCell<T, V>
              key={`${cell.id}-${rowIdx}`}
              cell={cell as Cell<T, V>}
              highLight={highLight}
            />
          );
        })}
      </Table.TableRow>
    </RightClickComp>
  );
};

export default TableRow;

export const EmptyTableRow = ({
  customProperties,
  getContent,
}: {
  customProperties: boolean;
  getContent: () =>
    | string
    | number
    | true
    | Iterable<React.ReactNode>
    | JSX.Element;
}): React.JSX.Element => {
  return (
    <Table.TableRow
      className={cn(
        "hover:bg-white dark:hover:bg-inherit",
        customProperties && "hidden",
      )}
    >
      <Table.TableCell colSpan={10} className='h-24 text-center'>
        {getContent()}
      </Table.TableCell>
    </Table.TableRow>
  );
};
